import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { ReactNode } from 'react';
import {
  CommandDashboardKeys,
  getRulesEngineTileMetrics,
} from '../../api/command-dashboard-api';
import HeroMetric from '../../shared/hero-metric';
import MetricStat, { MetricStatColor } from '../../shared/metric-stat';
import { Messages } from '../../utils/message-constants';
import {
  FULL_WIDTH_TILE_COLUMNS,
  HALF_WIDTH_TILE_COLUMNS,
  TILE_CONTENT_PADDING,
} from './tile-config';
import TileInsufficientAccessNotice from './tile-insufficient-access-notice';
import type { CommandDashboardTileBodyProps } from './tile-content-registry';
import { useCanSeeTile } from './use-tile-access';
import {
  useBindTileRefresh,
  useTrackCommandDashboardTileRefresh,
} from './use-tile-refresh';

import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import AssignmentOutlined from '@mui/icons-material/AssignmentOutlined';
import DisabledByDefaultOutlined from '@mui/icons-material/DisabledByDefaultOutlined';
import NoteAltOutlined from '@mui/icons-material/NoteAltOutlined';
import WarningAmberOutlined from '@mui/icons-material/WarningAmberOutlined';
import type { SxProps, Theme } from '@mui/material/styles';

const HERO_WIDTH = 255;
const HERO_BLOCK_INSET_LEFT = 16;
const COMPACT_CARD_PADDING = 16;
/**
 * Floor the hero column shrinks to at the half width.
 *
 * Figma's 799px tile fits a 255px hero beside two 228px cards exactly
 * (16 + 255 + 8 + 472 = 751 of content). Every pixel a real dashboard is
 * narrower than that comes out of the cards, and they have almost none to
 * give - so the hero gives some up first rather than the labels wrapping three
 * lines deep.
 */
const COMPACT_HERO_MIN_WIDTH = 180;
// Any wider squeezes the half-width 2x2 (751px content, 472px of cards) below
// its design width.
const HERO_CARDS_GAP = 8;
const FULL_TIER_CARD_COUNT = 4;
const COMPACT_TIER_CARD_COUNT = 2;
const SECONDARY_METRIC_WIDTH = 292;
const SECONDARY_METRIC_HEIGHT = 144;
const SECONDARY_METRIC_GAP_FULL = 16;
const SECONDARY_METRIC_GAP_NARROW = 16;
const SECONDARY_METRIC_ROW_HEIGHT = 95;
const SECONDARY_METRIC_ROW_PADDING = '8px 20px 8px 16px';
const ROW_TIER_SPACING = 24;

/** One layout per size the tile can be placed at. */
type LayoutTier = 'full' | 'compact' | 'row';

function layoutTierFor(columns: number): LayoutTier {
  if (columns >= FULL_WIDTH_TILE_COLUMNS) {
    return 'full';
  }
  return columns >= HALF_WIDTH_TILE_COLUMNS ? 'compact' : 'row';
}

function secondaryMetricsSx(tier: LayoutTier): SxProps<Theme> {
  if (tier === 'row') {
    return {
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      flex: 1,
      minHeight: 0,
      justifyContent: 'space-between',
      gap: '16px',
    };
  }
  if (tier === 'compact') {
    return {
      display: 'grid',
      gridTemplateColumns: `repeat(${COMPACT_TIER_CARD_COUNT}, minmax(0, 1fr))`,
      gridTemplateRows: 'repeat(2, minmax(0, 1fr))',
      gap: `${SECONDARY_METRIC_GAP_NARROW}px`,
      flex: 1,
      minWidth: 0,
      alignSelf: 'stretch',
    };
  }
  return {
    display: 'grid',
    gridTemplateColumns: `repeat(${FULL_TIER_CARD_COUNT}, minmax(0, ${SECONDARY_METRIC_WIDTH}px))`,
    gap: `${SECONDARY_METRIC_GAP_FULL}px`,
    flex: 1,
    minWidth: 0,
    justifyContent: 'flex-end',
  };
}

function metricStatSx(tier: LayoutTier): SxProps<Theme> {
  if (tier === 'row') {
    return {
      width: '100%',
      height: SECONDARY_METRIC_ROW_HEIGHT,
      flexShrink: 1,
      minHeight: 0,
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: 0,
      padding: SECONDARY_METRIC_ROW_PADDING,
    };
  }
  // Below Figma's canvas width the cards shrink and a label like "Rules
  // Triggered Today" takes two lines where its neighbour takes one. Pinning
  // the value to the bottom of the card keeps all four numbers on one line as
  // that happens, rather than each sitting wherever its own label left it.
  if (tier === 'compact') {
    return {
      // Fills its share of the 2x2 rather than setting it - no 228px cap, or
      // the card leaves its own track half empty once the grid is wider than
      // the cap allows.
      width: '100%',
      height: '100%',
      minHeight: 0,
      justifyContent: 'space-between',
      padding: `${COMPACT_CARD_PADDING}px`,
    };
  }
  return {
    width: '100%',
    maxWidth: SECONDARY_METRIC_WIDTH,
    minHeight: SECONDARY_METRIC_HEIGHT,
    justifyContent: 'space-between',
  };
}

function metricStatValueSx(tier: LayoutTier): SxProps<Theme> | undefined {
  if (tier === 'row') {
    return { typography: 'h5' };
  }
  return tier === 'compact' ? { typography: 'h4' } : undefined;
}

function metricStatLabelSx(tier: LayoutTier): SxProps<Theme> | undefined {
  return tier === 'compact' ? { typography: 'body2' } : undefined;
}

function metricStatHeaderSx(tier: LayoutTier): SxProps<Theme> | undefined {
  if (tier === 'compact') {
    return { flexDirection: 'column' };
  }
  return tier === 'row'
    ? { flexDirection: 'row', alignItems: 'center' }
    : undefined;
}

function metricValue(
  isFetching: boolean,
  value: number | undefined
): ReactNode {
  return isFetching ? <Skeleton width={40} /> : value ?? 0;
}

const SECONDARY_METRICS: {
  key: string;
  label: string;
  field:
    | 'rulesTriggeredToday'
    | 'draftRules'
    | 'rulesWithErrors'
    | 'rulesWithFailures';
  icon: typeof AssignmentOutlined;
  color: MetricStatColor;
}[] = [
  {
    key: 'triggeredToday',
    label: 'Rules Triggered Today',
    field: 'rulesTriggeredToday',
    icon: AssignmentOutlined,
    color: 'success',
  },
  {
    key: 'draftRules',
    label: 'Draft Rules',
    field: 'draftRules',
    icon: NoteAltOutlined,
    color: 'violet',
  },
  {
    key: 'rulesWithErrors',
    label: 'Rules with Errors',
    field: 'rulesWithErrors',
    icon: WarningAmberOutlined,
    color: 'warning',
  },
  {
    key: 'rulesWithFailures',
    label: 'Rules with Failures',
    field: 'rulesWithFailures',
    icon: DisabledByDefaultOutlined,
    color: 'error',
  },
];

export default function RulesEngineTileMetrics({
  tile,
  columns = FULL_WIDTH_TILE_COLUMNS,
}: CommandDashboardTileBodyProps) {
  const tier = layoutTierFor(columns);
  const isRowTier = tier === 'row';
  const hasPermission = useCanSeeTile(tile);
  const trackRefresh = useTrackCommandDashboardTileRefresh();

  const query = useQuery({
    queryKey: [CommandDashboardKeys.RulesEngineTile],
    queryFn: getRulesEngineTileMetrics,
    enabled: hasPermission,
    placeholderData: keepPreviousData,
    meta: { errorMessage: Messages.CommandDashboard.RulesEngineTileLoadError },
  });
  const metrics = query.data;
  const isFetching = query.isFetching;

  const onRefresh = async () => {
    const result = await query.refetch();
    if (!result.isError) {
      trackRefresh();
    }
  };
  useBindTileRefresh(tile, hasPermission, {
    onRefresh,
    isFetching,
  });

  if (!hasPermission && metrics === undefined) {
    return <TileInsufficientAccessNotice tile={tile} />;
  }

  return (
    <Box
      data-testid="rules-engine-metrics"
      sx={{
        flex: 1,
        minHeight: 0,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Stack
        sx={{
          flex: 1,
          minHeight: 0,
          boxSizing: 'border-box',
          ...(isRowTier
            ? {
                flexDirection: 'column',
                justifyContent: 'flex-start',
                alignItems: 'stretch',
                alignSelf: 'stretch',
                padding: `${ROW_TIER_SPACING}px`,
                gap: `${ROW_TIER_SPACING}px`,
              }
            : {
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: tier === 'compact' ? 'stretch' : 'center',
                alignSelf: 'stretch',
                gap: `${HERO_CARDS_GAP}px`,
                padding: `${TILE_CONTENT_PADDING}px`,
              }),
        }}
      >
        {/*
          No "Last Updated" caption here, deliberately: this count is
          computed live per request rather than read from a stored snapshot
          (same reasoning as Active Users), so the timestamp would only ever
          restate when the page was loaded.
        */}
        <HeroMetric
          label="Total Active Rules"
          value={
            isFetching ? <Skeleton width={60} /> : metrics?.activeRules ?? 0
          }
          sx={
            isRowTier
              ? { width: '100%' }
              : {
                  width: HERO_WIDTH,
                  ml: `${HERO_BLOCK_INSET_LEFT}px`,
                  alignSelf: tier === 'compact' ? 'stretch' : 'center',
                  ...(tier === 'compact'
                    ? { flexShrink: 1, minWidth: `${COMPACT_HERO_MIN_WIDTH}px` }
                    : { flexShrink: 0 }),
                }
          }
        />
        <Box sx={secondaryMetricsSx(tier)}>
          {SECONDARY_METRICS.map((metric) => (
            <MetricStat
              key={metric.key}
              icon={metric.icon}
              label={metric.label}
              value={metricValue(isFetching, metrics?.[metric.field])}
              color={metric.color}
              sx={metricStatSx(tier)}
              headerSx={metricStatHeaderSx(tier)}
              valueSx={metricStatValueSx(tier)}
              labelSx={metricStatLabelSx(tier)}
            />
          ))}
        </Box>
      </Stack>
    </Box>
  );
}
